import type { Client, Contact } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgreementListApi, useContactListApi } from '@hero/api-hooks';
import { closeAllDrawers, useClientWorkspaceStore } from '@hero/hooks-shared';
import { useNav } from '@hero/web-hooks';
import { Button } from '@hero/web-components';
import { ArrowRightIcon } from '@hero/web-icons';
import { SharedList } from '../../../app/List/List';
import { ContactCard } from '../../Contact/Card/ContactCard';
import { ClientPeekInfo, computeClientStatus, CompletenessBar } from '../ClientPeekInfo';

export interface ClientSummaryTabProps {
  client: Client;
}

/**
 * Drawer Info tab — completeness peek + "Open full client" CTA + Contacts list.
 * Composition over duplication: the peek body is the shared `ClientPeekInfo`.
 */
export function ClientSummaryTab({ client }: ClientSummaryTabProps) {
  const { t } = useTranslation('m-client');
  const { navigate } = useNav();

  // Surface the same agreement signal the workspace Info tab uses
  // so the drawer's `CompletenessBar` matches what the user sees
  // when they "Open full client". React-query dedupes against the
  // workspace's `AgreementInfoSection` query for this `partyId`.
  const agreementsListQuery = useAgreementListApi({ partyId: client._id });
  const hasOpenAgreement = useMemo<boolean | undefined>(() => {
    if (agreementsListQuery.isLoading || !agreementsListQuery.data) return undefined;
    return agreementsListQuery.data.some(
      a => a.status === 'new' || a.status === 'active',
    );
  }, [agreementsListQuery.data, agreementsListQuery.isLoading]);

  const status = computeClientStatus(client, { hasOpenAgreement });

  // Legacy filter param `customerId` still works (saved-location and
  // contact list endpoints accept the unified Client._id under that
  // name during the cutover).
  const contactsQuery = useContactListApi({ customerId: client._id });

  const handleOpenFull = () => {
    const clientId = String(getObjectId(client._id));
    // Dismiss the whole drawer chain (contact → client, order → client, …)
    // before leaving for the CRM workspace.
    closeAllDrawers();
    // Hydrate the workspace store immediately so selection updates even
    // when the user is already on `/crm` (URL-only nav can be a no-op
    // for React Router when the pathname is unchanged).
    useClientWorkspaceStore.getState().selectClient(clientId);
    navigate('crm', { clientId });
  };

  return (
    <div className="flex flex-col px-5 py-4 gap-5">
      <div className="flex flex-col gap-3">
        <CompletenessBar percent={status.overall} />
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenFull}
          className="w-full justify-center gap-2">
          {t('drawer.peek.open_full')}
          <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
        </Button>
      </div>

      <ClientPeekInfo
        client={client}
        hideCompleteness
        hideLinkedParty
        hasOpenAgreement={hasOpenAgreement}
      />

      <div className="mt-2">
        <h4 className="text-sm font-medium text-gray-500 mb-3">
          {t('drawer.tabs.contacts')}
        </h4>
        <SharedList<Contact>
          query={contactsQuery}
          emptyMessage={t('drawer.no_contacts')}
          className="gap-2"
          renderItem={contact => <ContactCard contact={contact} />}
        />
      </div>
    </div>
  );
}
